import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Select,
  Typography,
  Button,
  Row,
  Col,
  Breadcrumb,
  Empty,
  Image,
  Dropdown,
  Tooltip,
} from "antd";
import { WarningOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import CardMessage from "../../../components/Cards/CardMessage";
import SendNotification from "../../../components/Modals/SendNotification";
import SendEmail from "../../../components/Modals/SendEmail";
import logoHPCronic from "../../../assets/images/companies/Logo-Hospital-Virtual-Cronic.png";
import SendPill from "../../../components/Modals/SendPill";
import DashServices from "../../../services/DashServices";
import SectionLoader from "../../../components/Loaders/SectionLoader";
import UserServices from "../../../services/UserServices";
import { translate } from "../../../locale";
import previewImage from "../../../assets/images/icon_user.png";
import ModalDownloadResume from "../../../components/Modals/ModalDownloadResume";
import _ from "lodash";
import PropTypes from "prop-types";
import UnsubscribeButton from "./Helpers/UnsubscribeButton";

const { Option } = Select;
const { Title, Text } = Typography;

const SectionHeader = ({ title, onPeriodChange }) => {
  return (
    <div className="flex flex-col md:flex-row justify-start md:justify-center items-center w-full bg-gray-200 relative mt-16 pb-4 md:pb-0">
      <Title level={3} className="!text-base lg:!text-3xl mt-4 !mb-0 md:!mb-4">
        {title}
      </Title>
      {onPeriodChange && (
        <Select
          defaultValue="1"
          onChange={onPeriodChange}
          className="border-custom_primary mb-0 md:mb-3 lg:my-4 mr-4 relative md:absolute right-0 md:right-4 self-end"
        >
          <Option value="1">24 horas</Option>
          <Option value="7">7 días</Option>
          <Option value="30">30 días</Option>
          <Option value="180">6 meses</Option>
          <Option value="365">1 año</Option>
        </Select>
      )}
    </div>
  );
};

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  onPeriodChange: PropTypes.func,
};

const PatientResume = () => {
  const { username } = useParams();
  const [loading, setLoading] = useState({
    user: false,
    outOfRangeSelfMesure: false,
    registers: false,
    outOfRangeEvaluations: false,
    outRangeMedicines: false,
  });
  const [days, setDays] = useState({
    outOfRangeSelfMesure: 1,
    outOfRangeEvaluations: 1,
    outRangeMedicines: 1,
    interventions: 1,
  });
  const [user, setUser] = useState(null);
  const [registers, setRegisters] = useState([]);
  const [selfMesure, setSelfMesure] = useState([]);
  const [selfEvaluations, setSelfEvaluations] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [protocolDays, setProtocolDays] = useState([]);
  const [notesCounter, setNotesCounter] = useState(0);
  const [formatFile, setFormatFile] = useState("");
  const [modules, setModules] = useState(null);
  const [activeSections, setActiveSections] = useState({
    automediciones: true,
    diario_de_registros: true,
    autoevaluaciones: true,
    medicamentos: true,
  });
  const [visible, setVisible] = useState({
    sendEmail: false,
    sendNotification: false,
    sendPill: false,
    sendDoc: false,
  });
  const measureTypes = {
    sistolico: "Presión Sistólica",
    diastolico: "Presión Diastólica",
    fc: "Frecuencia Cardíaca",
    peso: "Peso",
    "diff-peso": "Aumento de peso",
    diabetes: "Glucemia",
    insulina: "Insulina",
    alimentos: "Alimentos",
    so2: "Saturación O2",
    temperatura: "Temperatura",
    fr: "Frecuencia Respiratoria",
  };
  const units = {
    // weight
    kg: "kg.",
    lb: "lb.",

    // diabetes
    mgdl: "mg/dl",

    // hypertension
    mmhg: "mg/hg",
    bpm: "bpm",
  };
  const navigate = useNavigate();

  const sectionsModules = {
    automediciones: [
      "Diabetes",
      "Hipertensión",
      "Pesos",
      "SaturacionO2",
      "Temperatura",
      "Podómetro",
    ],
    diario_de_registros: [
      "Alimentacion",
      "Agua",
      "Ejercitación",
      "manual_physical_activity",
      "Respiración",
      "notepad",
    ],
    autoevaluaciones: ["Autoevaluaciones"],
    medicamentos: ["Medicamentos"],
    intervenciones: ["Telemedicina"],
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadPatientInfo();
    getOutOfRangeSelfMesure(1);
    getRegisters(1);
    getOutOfRangeEvaluations(1);
    getOutRangeMedicines(1);
    getInterventions(1);
    loadNotesCounter();
  }, []);

  useEffect(() => {
    if (modules?.length > 0) {
      let sections = {
        automediciones: includesModule(sectionsModules.automediciones, modules),
        diario_de_registros: includesModule(
          sectionsModules.diario_de_registros,
          modules
        ),
        autoevaluaciones: includesModule(
          sectionsModules.autoevaluaciones,
          modules
        ),
        medicamentos: includesModule(sectionsModules.medicamentos, modules),
      };
      setActiveSections(sections);
    }
  }, [modules]);

  const setLoader = (name, value) => {
    setLoading((v) => ({ ...v, [name]: value }));
  };

  const includesModule = (defaultModules, modules) => {
    const filtered = _.filter(modules, (i) =>
      _.includes(defaultModules, i.name)
    );
    return filtered.length > 0;
  };

  const loadPatientInfo = async () => {
    try {
      setLoader("user", true);
      const response = await UserServices.getPatientInfo(username);
      if (response.status >= 200 && response.status < 300) {
        setProtocolDays(response.data.patient_info);
        loadSubscription(
          response.data.patient_info.map((i) => i.subscription.id)
        );
      }
      const res = await UserServices.getProfile(username);
      if (res.status >= 200 && res.status < 300) {
        setUser(res.data.my_contact_info);
      }
      setLoader("user", false);
    } catch (error) {
      setLoader("user", false);
      console.log(error);
    }
  };

  const loadSubscription = async (ids) => {
    setLoader("suscription", true);
    try {
      const fetchPromises = ids.map((i) => DashServices.getSubscriptions(i));
      const res = await Promise.all(fetchPromises);
      const data = await Promise.all(
        res.map((r) => {
          if (r.status != 200) return;
          return r.data.subscription.modules;
        })
      );
      let newModules = data[0];
      for (let i = 1; i < data.length; ++i) {
        const difference = _.unionBy(newModules, data[i], "name");
        newModules = difference;
      }
      setModules(newModules);
    } catch (error) {
      console.log(error);
    } finally {
      setLoader("suscription", false);
    }
  };

  const loadNotesCounter = async () => {
    try {
      setLoader("note_counter", true);
      const response = await UserServices.getNotesCounter(null, username);
      if (response.status >= 200 && response.status < 300) {
        setNotesCounter(response.data.count);
      }
      setLoader("note_counter", false);
    } catch (error) {
      setLoader("note_counter", false);
      console.log(error);
    }
  };

  const getOutOfRangeSelfMesure = async (days) => {
    try {
      setLoader("outOfRangeSelfMesure", true);
      const response = await DashServices.OutRangeRPM(days, "", username);
      if (response.status >= 200 && response.status < 300) {
        setSelfMesure(response.data.data);
      }
      setLoader("outOfRangeSelfMesure", false);
    } catch (error) {
      setLoader("outOfRangeSelfMesure", false);
      console.log(error);
    }
  };

  const getRegisters = async (days) => {
    try {
      setLoader("registers", true);
      let response = await DashServices.OutRangeFeeding(30, username);
      if (response.status >= 200 && response.status < 300) {
        setRegisters(
          response.data.data.map((i) => {
            let date_hour = "";
            if (i.days_ago > 0) {
              date_hour = i.days_ago + " días";
            }
            if (i.hours_ago > 0) {
              if (i.days_ago > 0) {
                date_hour += " ";
              }
              date_hour += i.hours_ago + " hrs";
            }
            return {
              title: "Alimentación",
              date: date_hour,
              type: i.status,
            };
          })
        );
      }
      setLoader("registers", false);
    } catch (error) {
      setLoader("registers", false);
      console.log(error);
    }
  };

  const getInterventions = async (days) => {
    try {
      setLoader("interventions", true);
      let response = await DashServices.GetInterventions(days, username);
      if (response.status >= 200 && response.status < 300) {
        setInterventions(response.data.data);
      }
      setLoader("interventions", false);
    } catch (error) {
      setLoader("interventions", false);
      console.log(error);
    }
  };

  const getOutOfRangeEvaluations = async (days) => {
    try {
      setLoader("outOfRangeEvaluations", true);
      let response = await UserServices.getOutRangeAutoevaluate(days, username);
      if (response.status >= 200 && response.status < 300) {
        let tmp = [];

        const danger = response.data.data[0].out_of_range.list_danger;
        const warning = response.data.data[0].out_of_range.list_warning;
        for (const element of danger) {
          tmp.push({
            type: "danger",
            title: element.title,
            register_date: element.register_date,
          });
        }

        for (const element of warning) {
          tmp.push({
            type: "warning",
            title: element.title,
            register_date: element.register_date,
          });
        }

        setSelfEvaluations(tmp);
      }
      setLoader("outOfRangeEvaluations", false);
    } catch (e) {
      setLoader("outOfRangeEvaluations", false);
      setSelfEvaluations([]);
    }
  };

  const getOutRangeMedicines = async (days) => {
    try {
      setLoader("outRangeMedicines", true);
      let response = await UserServices.getOutRangeMedicine(days, username);
      if (response.status >= 200 && response.status < 300) {
        setMedicines(response.data.data[0].medicines);
      }
      setLoader("outRangeMedicines", false);
    } catch (e) {
      setMedicines([]);
      setLoader("outRangeMedicines", false);
    }
  };

  const handleShowModal = (modal) => {
    setVisible((v) => ({ ...v, [modal]: true }));
  };

  const handleCancelModal = (modal) => {
    setVisible((v) => ({ ...v, [modal]: false }));
  };

  const handleCompleteModal = (modal) => {
    setVisible((v) => ({ ...v, [modal]: false }));
    getInterventions(days.interventions);
  };
  const handlePersonalDetails = () => {
    navigate(`/admin/buscar-paciente/${username}/personal-details`);
  };
  const handleProfessionalNotes = () => {
    navigate(`/admin/buscar-paciente/${username}/professional-notes`);
  };
  const changeDays = (name, value) => {
    setDays((v) => ({ ...v, [name]: value }));
  };
  const items = [
    {
      key: "1",
      label: `${translate("Descargar")} PDF`,
      onClick: () => setFormatFile("pdf"),
      style: { textAlign: "center" },
    },
    /* {
      key: "2",
      label: `${translate("Descargar")} CSV`,
      onClick: () => setFormatFile("csv"),
      style: { textAlign: "center" },
    }, */
  ];

  const renderIntervention = (item, index) => {
    const data = getInterventionData(item);
    return data.title ? (
      <Col xs={{ span: 24 }} sm={{ span: 12 }} lg={{ span: 6 }} key={index}>
        <CardMessage className="h-full" {...data} />
      </Col>
    ) : null;
  };

  const getInterventionData = (item) => {
    switch (item.type) {
      case "Notificacion":
        return getNotificationData(item);
      case "Email":
        return getEmailData(item);
      case "Pildora":
        return getPillData(item);
      default:
        return {
          title: "",
          description: "",
          buttonTitle: "",
          onClickButton: () => {},
          onClick: () => {},
        };
    }
  };

  const getNotificationData = (item) => ({
    title: (
      <>
        {translate("Notificaciones enviadas")}
        <br />
        {item.count}
      </>
    ),
    description: (
      <>
        {translate("Última notificación enviada")}
        <br />
        {item.last == null
          ? "Nunca"
          : moment(item.last).tz(moment.tz.guess()).format("DD/MM/YYYY HH:mm")}
      </>
    ),
    buttonTitle: "Enviar Notificación",
    onClickButton: () => handleShowModal("sendNotification"),
    onClick:
      item.last == null
        ? null
        : (e) =>
            navigate(
              `/admin/buscar-paciente/${username}/notifications/${days.interventions}`
            ),
  });

  const getEmailData = (item) => ({
    title: (
      <>
        {translate("Emails enviados")}
        <br />
        {item.count}
      </>
    ),
    description: (
      <>
        {translate("Último email enviado")}
        <br />
        {item.last == null
          ? "Nunca"
          : moment(item.last).tz(moment.tz.guess()).format("DD/MM/YYYY HH:mm")}
      </>
    ),
    buttonTitle: "Enviar Email",
    onClickButton: () => handleShowModal("sendEmail"),
    onClick:
      item.last == null
        ? null
        : (e) => {
            navigate(
              `/admin/buscar-paciente/${username}/emails/${days.interventions}`
            );
          },
  });

  const getPillData = (item) => ({
    title: (
      <>
        {translate("Píldoras enviadas")}
        <br />
        {item.count}
      </>
    ),
    description: (
      <>
        {translate("Última píldora enviada")}
        <br />
        {item.last == null
          ? "Nunca"
          : moment(item.last).tz(moment.tz.guess()).format("DD/MM/YYYY HH:mm")}
      </>
    ),
    buttonTitle: "Agregar Pildora",
    onClickButton: () => handleShowModal("sendPill"),
    onClick:
      item.last == null
        ? null
        : (e) => {
            navigate(
              `/admin/buscar-paciente/${username}/pills/${days.interventions}`
            );
          },
  });

  const handleUnsubscribeComplete = () => loadPatientInfo();

  return (
    <div className="flex flex-col min-h-full w-screen max-w-7xl pb-16 my-10 mx-0 lg:mx-10 px-4 lg:px-0">
      <Row gutter={16} className="mb-8">
        <Col
          className="gutter-row"
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 12 }}
        >
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/admin">Inicio</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Resumen individual</Breadcrumb.Item>
          </Breadcrumb>
          <Breadcrumb>
            <Breadcrumb.Item>{username}</Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col span={24} className="mt-4">
          <Title
            className="flex justify-center !text-xl lg:!text-3xl"
            level={2}
          >
            Resumen Individual
          </Title>
          <Text className="flex justify-center" level={5}>
            Información general
          </Text>
        </Col>
      </Row>

      <div className="flex flex-col mt-4 md:mt-6">
        <div>
          <Text className="text-lg md:text-2xl">Paciente: </Text>
          <div className="flex flex-1 items-center">
            <div className="w-16 h-16 overflow-hidden rounded-full flex items-center">
              <Image
                src={user?.photo ? user.photo : previewImage}
                className="w-16 h-16"
              />
            </div>
            <Text className="text-lg md:text-2xl ml-2">
              {user != null ? user.first_name + " " + user.last_name : ""}
            </Text>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-start md:justify-between mt-8 space-y-2 md:space-y-0">
          <Button
            type="primary"
            className="px-10 bg-white text-black"
            onClick={handlePersonalDetails}
          >
            Ficha personal
          </Button>
          <Button
            type="primary"
            className="px-10 bg-white text-black"
            onClick={handleProfessionalNotes}
          >
            Notas del profesional {!loading.note_counter && `(${notesCounter})`}
          </Button>
          <Dropdown menu={{ items }}>
            <Button
              type="primary"
              className="px-10 bg-white text-black"
              onClick={(e) => e.preventDefault()}
            >
              Informe del paciente
            </Button>
          </Dropdown>
          <ModalDownloadResume
            visible={formatFile !== ""}
            onClose={() => setFormatFile("")}
            formatFile={formatFile}
          />
        </div>
        <div className="w-full">
          <Row className="mt-10">
            <Col
              span={6}
              className="border-0 border-b border-solid border-black pb-4"
            >
              <div className="text-center">Suscripción</div>
            </Col>
            <Col
              span={7}
              className="border-0 border-b border-solid border-black pb-4"
            >
              <div className="text-center">Protocolo</div>
            </Col>
            <Col
              span={3}
              className="border-0 border-b border-solid border-black pb-4"
            >
              <div className="text-center">Días de protocolo</div>
            </Col>
            <Col
              span={3}
              className="border-0 border-b border-solid border-black pb-4"
            >
              <div className="text-center">Bloqueado</div>
            </Col>
            <Col
              span={3}
              className="border-0 border-b border-solid border-black pb-4"
            >
              <div className="text-center">Fecha de vencimiento</div>
            </Col>
            <Col
              span={2}
              className="border-0 border-b border-solid border-black pb-4"
            >
              <div className="text-center"></div>
            </Col>
          </Row>
          {protocolDays.length > 0 &&
            protocolDays.map((i, k) => (
              <Row key={i.subscription.id}>
                <Col span={6} className="mt-6">
                  <div className="text-center font-medium">
                    {i.subscription.name}
                  </div>
                </Col>
                <Col span={7} className="mt-6">
                  <div className="text-center font-medium">
                    {i.protocol.name}
                  </div>
                </Col>
                <Col span={3} className="mt-6">
                  <div className="text-center font-medium">
                    {i.days}
                    {i.protocol_user.is_paused && (
                      <Tooltip
                        title={translate(
                          "El usuario tiene pendiente cumplir una acción bloqueante."
                        )}
                      >
                        <WarningOutlined className="text-xl text-custom_yellow ms-2" />
                      </Tooltip>
                    )}
                  </div>
                </Col>
                <Col span={3} className="mt-6">
                  {i.protocol_user.is_paused && (
                    <div className="text-center font-medium">
                      <span className="font-bold">
                        {translate("Fecha de bloqueo")}
                      </span>
                      :{" "}
                      {moment(i.protocol_user.datetime_paused)
                        .tz(moment.tz.guess())
                        .format("DD/MM/YYYY")}
                      {i.protocol_user.block_reasons.map((item, index) => (
                        <div key={item + index}>{item}</div>
                      ))}
                    </div>
                  )}
                  {!i.protocol_user.is_paused && (
                    <div className="text-center font-medium">
                      {translate("No")}
                    </div>
                  )}
                </Col>
                <Col span={3} className="mt-6 text-center">
                  {i.subscription.end_date
                    ? moment(i.subscription.end_date)
                        .tz(moment.tz.guess())
                        .format("DD/MM/YYYY")
                    : "-"}
                </Col>
                <Col span={2} className="mt-6 text-center">
                  <UnsubscribeButton
                    subscription_id={i.subscription.id}
                    username={username}
                    onComplete={handleUnsubscribeComplete}
                    disabled={i.subscription.ended}
                  />
                </Col>
              </Row>
            ))}
        </div>
      </div>
      {activeSections.automediciones && (
        <div className="relative">
          <SectionHeader
            title={translate("AUTOMEDICIONES FUERA DE RANGO")}
            onPeriodChange={(value) => {
              getOutOfRangeSelfMesure(value);
              changeDays("outOfRangeSelfMeasure", value);
            }}
          />

          <div className="flex flex-col items-start mx-4">
            <button
              onClick={() =>
                navigate(
                  "/admin/buscar-paciente/" + username + "/automediciones",
                  {
                    state: {
                      subscriptions: protocolDays.map((i) => i.subscription),
                    },
                  }
                )
              }
              className="mt-4 text-black underline hover:text-custom_primary bg-transparent border-none cursor-pointer"
            >
              {translate("Ver todo")}
            </button>
          </div>

          {selfMesure.length === 0 ? (
            <div className="flex justify-center flex-col items-center relative min-h-80">
              {loading.outOfRangeSelfMesure ? (
                <SectionLoader />
              ) : (
                <Text className="text-2xl">
                  <Empty description={translate("No hay datos")} />
                </Text>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-start mb-12">
              <Row gutter={[16, 40]} className="mt-4 w-full">
                {selfMesure.length > 0 &&
                  selfMesure[0].out_of_range?.list_danger.map((item, index) => (
                    <Col
                      xs={{ span: 24 }}
                      sm={{ span: 12 }}
                      lg={{ span: 6 }}
                      key={
                        moment(item.register_date).format("YYYYMMDDHHmmss") +
                        index
                      }
                    >
                      <CardMessage
                        className="mx-4"
                        title={
                          <>
                            {measureTypes[item.type]}
                            <br />
                          </>
                        }
                        description={
                          <>
                            {item.value} {units[item.unit]}
                            <br />
                            Última medición realizada
                            <br />
                            {item.register_date == null
                              ? "Nunca"
                              : moment(item.measure_datetime)
                                  .tz(moment.tz.guess())
                                  .format("DD/MM/YYYY HH:mm")}
                          </>
                        }
                        icon={
                          <ExclamationCircleOutlined className="text-3xl text-custom_red" />
                        }
                      />
                    </Col>
                  ))}
                {selfMesure.length > 0 &&
                  selfMesure[0].out_of_range?.list_warning.map(
                    (item, index) => (
                      <Col
                        xs={{ span: 24 }}
                        sm={{ span: 12 }}
                        lg={{ span: 6 }}
                        key={
                          moment(item.register_date).format("YYYYMMDDHHmmss") +
                          index
                        }
                      >
                        <CardMessage
                          className="mx-4"
                          title={
                            <>
                              {measureTypes[item.type]}
                              <br />
                            </>
                          }
                          description={
                            <>
                              {item.value} {units[item.unit]}
                              <br />
                              Última medición realizada
                              <br />
                              {item.register_date == null
                                ? "Nunca"
                                : moment(item.measure_datetime)
                                    .tz(moment.tz.guess())
                                    .format("DD/MM/YYYY HH:mm")}
                            </>
                          }
                          icon={
                            <WarningOutlined className="text-4xl text-custom_yellow" />
                          }
                        />
                      </Col>
                    )
                  )}
              </Row>
            </div>
          )}
        </div>
      )}

      {activeSections.diario_de_registros && (
        <div className="relative">
          <SectionHeader title={translate("DIARIO DE REGISTROS")} />

          <div className="flex flex-col items-start mx-4">
            <button
              onClick={() =>
                navigate(
                  "/admin/buscar-paciente/" + username + "/diario-registro"
                )
              }
              className="mt-4 text-black underline hover:text-custom_primary bg-transparent border-none cursor-pointer"
            >
              {translate("Ver todo")}
            </button>
          </div>

          {registers.length == 0 ? (
            <div className="flex justify-center flex-col items-center relative min-h-80">
              {loading.registers ? (
                <SectionLoader />
              ) : (
                <Text className="text-2xl">
                  <Empty description={translate("No hay datos")} />
                </Text>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-start mb-12">
              <Row gutter={[16, 40]} className="mt-4 w-full">
                {registers.map((item, index) => (
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    lg={{ span: 6 }}
                    key={item.title + index}
                  >
                    <CardMessage
                      className="mx-4"
                      title={
                        <>
                          {item.title}
                          <br />
                        </>
                      }
                      description={
                        <>
                          Último registro:
                          <br />
                          {item.date}
                        </>
                      }
                      icon={
                        item.type === "warning" ? (
                          <WarningOutlined className="text-4xl text-custom_yellow" />
                        ) : (
                          <ExclamationCircleOutlined className="text-3xl text-custom_red" />
                        )
                      }
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </div>
      )}

      {activeSections.autoevaluaciones && (
        <div className="relative">
          <SectionHeader
            title={translate("AUTOEVALUACIONES FUERA DE RANGO")}
            onPeriodChange={(value) => {
              getOutOfRangeEvaluations(value);
              changeDays("outOfRangeEvaluations", value);
            }}
          />
          <div className="flex flex-col items-start mx-4">
            <button
              onClick={() =>
                navigate(
                  "/admin/buscar-paciente/" + username + "/autoevaluaciones"
                )
              }
              className="mt-4 text-black underline hover:text-custom_primary bg-transparent border-none cursor-pointer"
            >
              {translate("Ver todo")}
            </button>
          </div>
          {selfEvaluations.length == 0 ? (
            <div className="flex justify-center flex-col items-center relative min-h-80">
              {loading.outOfRangeEvaluations ? (
                <SectionLoader />
              ) : (
                <Text className="text-2xl">
                  <Empty description={translate("No hay datos")} />
                </Text>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-start mb-12 relative">
              {loading.outOfRangeEvaluations && (
                <SectionLoader className="min-h-80" />
              )}
              <Row gutter={[16, 40]} className="mt-4 w-full">
                {selfEvaluations.map((item, index) => (
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    lg={{ span: 6 }}
                    key={
                      moment(item.register_date)
                        .tz(moment.tz.guess())
                        .format("DD/MM/YYYY HH:mm") + index
                    }
                  >
                    <CardMessage
                      className="mx-4"
                      title={
                        <>
                          {item.title}
                          <br />
                        </>
                      }
                      description={
                        <>
                          Fecha
                          <br />
                          {moment(item.register_date)
                            .tz(moment.tz.guess())
                            .format("DD/MM/YYYY HH:mm")}
                        </>
                      }
                      icon={
                        item.type === "warning" ? (
                          <WarningOutlined className="text-4xl text-custom_yellow" />
                        ) : (
                          <ExclamationCircleOutlined className="text-3xl text-custom_red" />
                        )
                      }
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </div>
      )}

      {activeSections.medicamentos && (
        <div className="relative">
          <SectionHeader
            title={translate("MEDICAMENTOS FUERA DE RANGO")}
            onPeriodChange={(value) => {
              getOutRangeMedicines(value);
              changeDays("outRangeMedicines", value);
            }}
          />
          <div className="flex flex-col items-start mx-4">
            <button
              onClick={() =>
                navigate(
                  "/admin/buscar-paciente/" + username + "/medicinas-paciente"
                )
              }
              className="mt-4 text-black underline hover:text-custom_primary bg-transparent border-none cursor-pointer"
            >
              {translate("Ver todo")}
            </button>
          </div>

          {medicines.length == 0 ? (
            <div className="flex justify-center flex-col items-center relative min-h-80">
              {loading.outRangeMedicines ? (
                <SectionLoader />
              ) : (
                <Text className="text-2xl">
                  <Empty description={translate("No hay datos")} />
                </Text>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-start mb-12 relative min-h-80">
              {loading.outRangeMedicines && (
                <SectionLoader className="min-h-80" />
              )}
              <Row gutter={[16, 40]} className="mt-4 w-full">
                {medicines.map((item, index) => (
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    lg={{ span: 6 }}
                    key={
                      moment(item.last_mt_tomado_date_time).format(
                        "YYYYMMDDHHmmss"
                      ) + index
                    }
                  >
                    <CardMessage
                      className="mx-4"
                      title={
                        <>
                          {item.medicine_name}
                          <br />
                        </>
                      }
                      description={
                        <>
                          Último registro
                          <br />
                          {moment(item.last_mt_tomado_date_time)
                            .tz(moment.tz.guess())
                            .format("DD/MM/YYYY HH:mm")}
                        </>
                      }
                      icon={
                        item.type === "warning" ? (
                          <WarningOutlined className="text-4xl text-custom_yellow" />
                        ) : (
                          <ExclamationCircleOutlined className="text-3xl text-custom_red" />
                        )
                      }
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </div>
      )}

      <div className="relative">
        <SectionHeader
          title={translate("INTERVENCIONES")}
          onPeriodChange={(value) => {
            getInterventions(value);
            changeDays("interventions", value);
          }}
        />

        <Row className="mt-4" gutter={[16, 40]}>
          {loading.interventions && (
            <Col span={24} className="text-center min-h-96">
              <SectionLoader />
            </Col>
          )}

          {interventions !== undefined && interventions.map(renderIntervention)}
          {user?.user.provider === "cronic" && (
            <Col xs={{ span: 24 }} sm={{ span: 12 }} lg={{ span: 6 }}>
              <CardMessage
                /* title={
              <>
                Contactos realizados
                <br />0
              </>
            }
            description={
              <>
                Último contacto enviado
                <br />
                Nunca
              </>
            }
            buttonTitle="Enviar contacto"
            onClickButton={() => {
              alert("click button");
            }}
            */
                className="h-full"
                title={translate("Contactos")}
                buttonTitle="Ver contactos"
                onClickButton={() => {
                  window.open("https://www.hospitalvirtual.com/");
                }}
                icon={
                  <Image src={logoHPCronic} className="h-16" preview={false} />
                }
              />
            </Col>
          )}
        </Row>
      </div>

      <SendEmail
        patient={username}
        visible={visible.sendEmail}
        onCancel={() => handleCancelModal("sendEmail")}
        onComplete={() => handleCompleteModal("sendEmail")}
      />
      <SendNotification
        patient={username}
        visible={visible.sendNotification}
        onCancel={() => handleCancelModal("sendNotification")}
        onComplete={() => handleCompleteModal("sendNotification")}
      />
      <SendPill
        patient={username}
        visible={visible.sendPill}
        onCancel={() => handleCancelModal("sendPill")}
        onComplete={() => handleCompleteModal("sendPill")}
      />
    </div>
  );
};

export default PatientResume;
